import { useStudioPermissions } from "@certa/queries/hooks/studio.hook";
import { isDev } from "../utils/isDev";
import { useIsWebGLStudio } from "../../studio/src/hooks/useStudioAPI/hooks/useIsStudioWebGL";
import { useIsStudioV3Enabled } from "../toggles/useDesignAIToggles";
import { useAuth } from "@certa/auth/AuthenticationProvider";
import { useInstanceConfigQuery } from "@certa/queries/hooks/config.hook";

const STUDIO2_APPROVAL_GATES = "STUDIO2_APPROVAL_GATES";
const STUDIO2_APP_SETTINGS = "STUDIO2_APP_SETTINGS";
const ENABLE_STUDIO_ATD_CONTROLS = "ENABLE_STUDIO_ATD_CONTROLS";
const STUDIO_TABLE_JSON_EDITOR = "STUDIO2_TABLE_CONFIG";
const STUDIO_BRANCH = "STUDIO_BRANCH";
const ENABLE_DASHBOARD_ASSIST = "ENABLE_DASHBOARD_ASSIST";
// const STUDIO_PROCESS_ACTIVITY_LOGS_ENABLED =
//   "STUDIO_PROCESS_ACTIVITY_LOGS_ENABLED";
const STUDIO2_SAVE_AS_TEMPLATE = "STUDIO2_SAVE_AS_TEMPLATE";
const STUDIO_OLD_PP = "STUDIO_OLD_PP"; // show old Properties pane on Studio WebGL
const OLD_STUDIO_ACCESS = "OLD_STUDIO_ACCESS";
const STUDIO_NEW_RULES_UI = "STUDIO_NEW_RULES_UI";
const ENABLE_SDO_IN_STUDIO = "ENABLE_SDO_IN_STUDIO";
const AUTO_SAVE_ON_THE_FLY = "AUTO_SAVE_ON_THE_FLY";
const STUDIO_FLOW_MODE = "STUDIO_FLOW_MODE";
const ENABLE_STRUCTURE_VIEW_FOR_PREVIEW = "ENABLE_STRUCTURE_VIEW_FOR_PREVIEW";
// OS3 FLow View and Suites
const ENABLE_SUITES_IN_STUDIO = "ENABLE_SUITES_IN_STUDIO";
const ENABLE_START_WITH_TEMPLATE_IN_STUDIO =
  "ENABLE_START_WITH_TEMPLATE_IN_STUDIO";
const DISABLE_OS3_FLOW_VIEW = "DISABLE_OS3_FLOW_VIEW";
const ENABLE_RULES_PANEL_IN_STUDIO = "ENABLE_RULES_PANEL_IN_STUDIO";
const AUDIT_LOGS_ENABLED = "AUDIT_LOGS_ENABLED";
// const DISABLE_APP_SETTINGS_THREE_WAY_MERGE =
//   "DISABLE_APP_SETTINGS_THREE_WAY_MERGE";
const ENABLE_APP_SETTINGS_THREE_WAY_MERGE =
  "ENABLE_APP_SETTINGS_THREE_WAY_MERGE";
const DISABLE_NEW_DEPLOYMENT_UX = "DISABLE_NEW_DEPLOYMENT_UX";

export const useCanViewStudio2 = (): { data: boolean; isLoading: boolean } => {
  const { data: permissions, isLoading: isPermissionsLoading } =
    useStudioPermissions();
  const { data: config } = useInstanceConfigQuery();
  const isStudioEnabled: boolean = !!config?.configuration.enable_studio;

  const canSeeStudio2Permissions = !!permissions?.canAccessStudio;
  return {
    data: canSeeStudio2Permissions && isStudioEnabled,
    isLoading: isPermissionsLoading
  };
};

export const useIsApprovalGatesEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isApprovalGatesEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(
      STUDIO2_APPROVAL_GATES
    );
  return isApprovalGatesEnabled;
};

export const useIsStudioATDEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isATDEnabled = !!config?.custom_ui_labels.specialFeatures?.includes(
    ENABLE_STUDIO_ATD_CONTROLS
  );
  return isATDEnabled;
};

export const useIsAppSettingsEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isAppSettingsEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(STUDIO2_APP_SETTINGS);
  return isAppSettingsEnabled;
};

export const useShowTableJSONEditor = () => {
  const { data: config } = useInstanceConfigQuery();
  const isStudioTableJSONEditorEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(
      STUDIO_TABLE_JSON_EDITOR
    );

  return isStudioTableJSONEditorEnabled;
};

export const useStudioBranchingEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isStudioBranchingEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(STUDIO_BRANCH);
  return isStudioBranchingEnabled;
};

export const useIsDashboardAssistEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isDashboardAssistEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(
      ENABLE_DASHBOARD_ASSIST
    );

  return (
    !["certain", "certaeu", "certasite"].includes(
      import.meta.env.REACT_APP_CERTA_ENV || ""
    ) && isDashboardAssistEnabled
  );
};

export const useIsStudioSaveAsTemplateEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isStudioSaveAsTemplateEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(
      STUDIO2_SAVE_AS_TEMPLATE
    );

  return isStudioSaveAsTemplateEnabled;
};

export const useIsOldDrawerEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(STUDIO_OLD_PP);

  return isEnabled;
};

export const useCanAccessOldStudio = () => {
  const { user: currentUser } = useAuth();
  const userEmail = currentUser?.email;
  const { data: config } = useInstanceConfigQuery();
  const oldStudioAccessRegexWhiteList =
    config?.custom_ui_labels?.[`${OLD_STUDIO_ACCESS}`] || [];

  if (isDev) {
    return true;
  }

  if (oldStudioAccessRegexWhiteList.length === 0 || !userEmail) return false;

  try {
    for (const regex of oldStudioAccessRegexWhiteList) {
      if (new RegExp(regex).test(userEmail)) {
        return true;
      }
    }
  } catch (error) {
    // Handle regex compilation errors, e.g., logging or returning false
    console.log("Invalid regex:", error);
    return false;
  }
  return false;
};

export const useCanViewNewRulesUI = () => {
  const { data: config } = useInstanceConfigQuery();
  const canViewNewRulesUI =
    !!config?.custom_ui_labels.specialFeatures?.includes(STUDIO_NEW_RULES_UI);
  return isDev ? true : canViewNewRulesUI;
};

export const useCanViewStudioObjects = () => {
  const { data: config } = useInstanceConfigQuery();
  const canViewStudioObjects =
    !!config?.custom_ui_labels.specialFeatures?.includes(ENABLE_SDO_IN_STUDIO);
  const isStudioEnabled = !!config?.configuration.enable_studio;
  return isDev || (canViewStudioObjects && isStudioEnabled);
};

export const useIsECAUIEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isECAEnabled = !!config?.feature_toggles.USE_ETERNITY_RULE_ENGINE;

  return isECAEnabled;
};

export const useIsATDEnabled = () => {
  // BE Toggle is not returned so using an FE toggle for now
  // const isATDEnabled = useSelector((state: ReduxState) =>
  //   lodashGet(
  //     state,
  //     "config.feature_toggles.USE_ATD_WF_MIGRATION_SCHEDULER",
  //     false
  //   )
  // );
  return useIsStudioATDEnabled();
};

export const useIsAutoSaveOnTheFlyEnabledForStudioWebGL = () => {
  const { data: config } = useInstanceConfigQuery();
  const isAutoSaveOnTheFlyEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(AUTO_SAVE_ON_THE_FLY);
  const isStudioWebGL = useIsWebGLStudio();

  // true as we are using inverse of this flag. Old studio the feature is disabled
  if (!isStudioWebGL) return true;

  // false as we are using inverse of this flag. New studio the feature is enabled
  if (isDev) return false;

  return isAutoSaveOnTheFlyEnabled && isStudioWebGL;
};

export const useIsStudioActivityLogsEnabled = () => {
  // Disabling activity logs for now, as it needs to be udpated with megaJSON
  // @abir: https://thevetted.atlassian.net/browse/DS-7390
  return false;
  // const isDesignerActivityLogsEnabled = useSelector((state: ReduxState) =>
  //   lodashGet(state, "config.custom_ui_labels.specialFeatures", []).includes(
  //     STUDIO_PROCESS_ACTIVITY_LOGS_ENABLED
  //   )
  // );
  // return isDesignerActivityLogsEnabled;
};

export const useIsStudioFlowModeEnabled = (): boolean => {
  const { data: config } = useInstanceConfigQuery();
  const isStudioFlowModeEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(STUDIO_FLOW_MODE);

  const isECAEnabled = useIsECAUIEnabled();
  // Flow mode should be disabled in OS3
  return !!isStudioFlowModeEnabled && !isECAEnabled;
};

// Note: This flag is not encourage to be used, Just as a fallback to unblock users/debugging tool
export const useIsStructureViewForPreviewEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isStructureViewForPreviewEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(
      ENABLE_STRUCTURE_VIEW_FOR_PREVIEW
    );

  return isStructureViewForPreviewEnabled;
};

export const useIsOS3FlowViewEnabled = () => {
  const isECAEnabled = useIsECAUIEnabled();
  const { data: config } = useInstanceConfigQuery();
  const isOS3FlowViewDisabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(DISABLE_OS3_FLOW_VIEW);

  return !isOS3FlowViewDisabled && isECAEnabled;
};

export const useIsSuitesEnabled = () => {
  const isECAEnabled = useIsECAUIEnabled();
  const { data: config } = useInstanceConfigQuery();
  const isSuitesEnabled = !!config?.custom_ui_labels.specialFeatures?.includes(
    ENABLE_SUITES_IN_STUDIO
  );
  return isDev || (isSuitesEnabled && isECAEnabled);
};

export const useIsStartWithTemplateEnabled = () => {
  const isStudioV3Enabled = useIsStudioV3Enabled();
  const { data: config } = useInstanceConfigQuery();
  const isStartWithTemplateEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(
      ENABLE_START_WITH_TEMPLATE_IN_STUDIO
    );

  // Other then v3, we want to enable this feature by default
  if (isStudioV3Enabled) {
    return isStartWithTemplateEnabled;
  }
  return true;
};

export const useIsRulesPanelInStudioEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isRulesPanelInStudioEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(
      ENABLE_RULES_PANEL_IN_STUDIO
    );

  return isRulesPanelInStudioEnabled || isDev;
};

export const useIsAuditLogsEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isAuditLogsEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(AUDIT_LOGS_ENABLED);
  return isAuditLogsEnabled || isDev;
};
export const useIsAppSettingsThreeWayMergeEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isAppSettingsThreeWayMergeDisabled = true;
  // const isAppSettingsThreeWayMergeDisabled = useSelector((state: ReduxState) =>
  //   lodashGet(state, "config.custom_ui_labels.specialFeatures", []).includes(
  //     DISABLE_APP_SETTINGS_THREE_WAY_MERGE
  //   )
  // );

  const isAppSettingsThreeWayMergeEnabled =
    !!config?.custom_ui_labels.specialFeatures?.includes(
      ENABLE_APP_SETTINGS_THREE_WAY_MERGE
    );

  const isOS3Enabled = useIsECAUIEnabled();

  return (
    // By default Enabled for OS3 Clients, unless explicitly disabled
    (isOS3Enabled && !isAppSettingsThreeWayMergeDisabled) ||
    // Disabled for OS2 Clients, unless explicitly enabled
    isAppSettingsThreeWayMergeEnabled
  );
};

export const useIsNewDeploymentUXEnabled = () => {
  const { data: config } = useInstanceConfigQuery();
  const isECAEnabled = useIsECAUIEnabled();

  const isNewDeploymentUXDisabled =
    config?.custom_ui_labels.specialFeatures?.includes(
      DISABLE_NEW_DEPLOYMENT_UX
    );

  return !isNewDeploymentUXDisabled && isECAEnabled;
};
